"use client";

import type { LanguageMetadata } from "@mw/shared/languagesAndAccents";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useRouter } from "@/i18n/navigation";
import { type DisconnectionDetails, useConversation } from "@elevenlabs/react";
import { getErrorMessageFromUnknownError } from "@utilities/errors";
import { useEffect, useRef, useState } from "react";
import { useConvAILanguage } from "@ui/hooks/useConvAILanguage";

export interface CallManagerState {
  callStartTime: null | number;
  callStarted: boolean;
  elapsed: number;
  getInputVolume?: () => number;
  getOutputVolume?: () => number;
  isMinimized: boolean;
  language: LanguageMetadata;
  muted: boolean;
  status: string;
}

export interface CallManagerActions {
  endCall: () => Promise<void>;
  setIsMinimized: (minimized: boolean) => void;
  setLanguage: (language: LanguageMetadata) => void;
  setMuted: (muted: boolean) => void;
  startCall: () => Promise<void>;
}

export function useCallManager(
  agentId: string,
  options: { variant?: string; autoStart?: boolean } = {},
): [CallManagerState, CallManagerActions] {
  // TODO(agent-controller-refactor): migrate duplicated terms/session orchestration to shared controller.
  const router = useRouter();
  const [callStarted, setCallStarted] = useState(false);
  const [callStartTime, setCallStartTime] = useState<null | number>(null);
  const [elapsed, setElapsed] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [muted, setMuted] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<null | number>(null);
  const { language, setLanguage: setSelectedLanguage } = useConvAILanguage();

  const { startSession, endSession, status, getInputVolume, getOutputVolume } = useConversation({
    micMuted: muted,
    clientTools: {
      redirectToPage: ({ path }: { path: string }) => {
        router.push(path);
      },
    },
    onDisconnect: (details: DisconnectionDetails & { error?: unknown }) => {
      if (details.reason === "error") {
        captureEvent(EventName.marketing_site_convai_agent_call_failed, {
          error_message:
            details.message ?? getErrorMessageFromUnknownError(details.error ?? details.context ?? details),
        });
      }
    },
    onMessage: message => {
      captureEvent(EventName.marketing_site_convai_agent_message_sent, {
        sender_role: message.role,
        message_type: "voice",
      });
    },
  });

  const startAgentSession = async (targetLanguage: string) => {
    let sessionId: string;
    try {
      sessionId = await startSession({
        agentId,
        overrides: {
          agent: {
            language: targetLanguage as any,
          },
        },
        connectionType: "webrtc",
      });
    } catch (error) {
      captureEvent(EventName.marketing_site_convai_agent_call_failed, {
        error_message: getErrorMessageFromUnknownError(error),
      });
      throw error;
    }

    // Store session ID for tracking
    sessionIdRef.current = sessionId;

    // Fire event AFTER we have the conversation_id
    captureEvent(EventName.marketing_site_convai_agent_call_started, {
      conversation_type: "voice",
      conversation_id: sessionId,
      agent_variant: options.variant || "floating",
      language: targetLanguage,
    });

    setCallStarted(true);
    setCallStartTime(Date.now());
  };

  useEffect(() => {
    callStartTimeRef.current = callStartTime;
  }, [callStartTime]);

  // Auto-start on mount if enabled, cleanup on unmount
  useEffect(() => {
    if (options.autoStart) {
      startAgentSession(language.value);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const start = callStartTimeRef.current;
      if (start != null) {
        const durationSecs = Math.floor((Date.now() - start) / 1000);
        captureEvent(EventName.marketing_site_convai_agent_call_ended, {
          call_duration_secs: durationSecs,
        });
      }
      endSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer logic
  useEffect(() => {
    if (status === "connected" || status === "connecting") {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setElapsed(callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : 0);
        }, 1000);
      }
    } else {
      setElapsed(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status, callStartTime]);

  // End call handler
  const handleEndCall = async () => {
    const durationSecs = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : null;
    captureEvent(EventName.marketing_site_convai_agent_call_ended, {
      call_duration_secs: durationSecs,
    });
    await endSession();
    setCallStarted(false);
    setCallStartTime(null);
  };

  const handleLanguageChange = async (newLanguage: LanguageMetadata) => {
    const durationSecs = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : null;
    setSelectedLanguage(newLanguage);
    captureEvent(EventName.marketing_site_convai_agent_call_ended, {
      call_duration_secs: durationSecs,
    });
    await endSession();
    await startAgentSession(newLanguage.value);
  };

  const state: CallManagerState = {
    callStarted,
    callStartTime,
    elapsed,
    isMinimized,
    muted,
    language,
    status,
    getInputVolume,
    getOutputVolume,
  };

  const handleStartCall = async () => {
    if (!callStarted) {
      await startAgentSession(language.value);
    }
  };

  const actions: CallManagerActions = {
    setIsMinimized,
    setMuted: newMuted => setMuted(newMuted),
    setLanguage: handleLanguageChange,
    endCall: handleEndCall,
    startCall: handleStartCall,
  };

  return [state, actions];
}
